import { useEffect, useRef, useState } from "react";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  getMasterDataListCacheEntry,
  masterDataListCacheKey,
  setMasterDataListCacheEntry,
  type MasterDataListCacheScope,
} from "./masterDataListCache";
import {
  useMasterDataListPagination,
  type MasterDataPageResult,
} from "./useMasterDataListPagination";

export interface MasterDataListFetchArgs {
  search: string | null;
  page: number;
  limit: number;
  filtersKey?: string;
}

export interface MasterDataListFetchResult<TItem> extends MasterDataPageResult {
  items: TItem[];
}

interface UseMasterDataListQueryOptions<TItem> {
  brandKey: string;
  defaultPageSize: number;
  errorMessage: string;
  cacheScope: MasterDataListCacheScope;
  /** Stable string of applied filters; included in cache key and fetch deps. */
  filtersKey?: string;
  fetchPage: (
    args: MasterDataListFetchArgs,
  ) => Promise<MasterDataListFetchResult<TItem>>;
}

/**
 * Shared Master Data list load cycle with Users Access–style page cache:
 * set X-Brand-Key → cache hit or paginated fetch → items + pagination state.
 * Cache survives submenu remounts; cleared when leaving Master Data layout.
 * `refetch()` forces a network reload for the current page key.
 */
export function useMasterDataListQuery<TItem>({
  brandKey,
  defaultPageSize,
  errorMessage,
  cacheScope,
  filtersKey = "",
  fetchPage,
}: UseMasterDataListQueryOptions<TItem>) {
  const initialCacheKey = masterDataListCacheKey(
    cacheScope,
    brandKey,
    1,
    defaultPageSize,
    "",
    filtersKey,
  );
  const initialCached =
    getMasterDataListCacheEntry<TItem>(initialCacheKey);

  const {
    loading,
    setLoading,
    error,
    setError,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    reloadToken,
    refetch,
    applyPageResult,
    applyLoadError,
  } = useMasterDataListPagination(brandKey, defaultPageSize, {
    initialLoading: !initialCached,
    initialTotal: initialCached?.total ?? 0,
  });

  const [items, setItems] = useState<TItem[]>(
    () => initialCached?.items ?? [],
  );
  const lastReloadTokenRef = useRef(0);

  useEffect(() => {
    MasterDataGraphQlAdapter.getInstance().setBrandKey(brandKey);
    return () => {
      MasterDataGraphQlAdapter.getInstance().setBrandKey(null);
    };
  }, [brandKey]);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = masterDataListCacheKey(
      cacheScope,
      brandKey,
      page,
      limit,
      search,
      filtersKey,
    );
    const forceRefresh = reloadToken !== lastReloadTokenRef.current;
    const cached = getMasterDataListCacheEntry<TItem>(cacheKey);

    if (cached && !forceRefresh) {
      setItems(cached.items);
      applyPageResult(cached);
      setLoading(false);
      setError(null);
      return;
    }

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchPage({
          search: search.trim() || null,
          page,
          limit,
          filtersKey,
        });
        if (cancelled) return;
        setMasterDataListCacheEntry(cacheKey, result);
        lastReloadTokenRef.current = reloadToken;
        setItems(result.items);
        applyPageResult(result);
      } catch (err) {
        if (cancelled) return;
        setItems([]);
        applyLoadError(err, errorMessage);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    brandKey,
    cacheScope,
    fetchPage,
    filtersKey,
    reloadToken,
    page,
    limit,
    search,
    errorMessage,
    setLoading,
    setError,
    applyPageResult,
    applyLoadError,
  ]);

  return {
    items,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    limit,
    total,
    refetch,
  };
}
